import React from "react";
import { Button, ButtonProps, CircularProgress } from "@mui/material";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";

type PopButtonPrimaryProps = Omit<ButtonProps, "palette"> & {
  popVariant?: "primary" | "white";
  isLoading?: boolean;
};

export const PopButtonPrimary: React.FC<PopButtonPrimaryProps> = (props) => (
  <PopButton palette="Blue" {...props} />
);

interface PopButtonProps extends ButtonProps {
  palette: PaletteColor;
  popVariant?: "primary" | "white";
  isLoading?: boolean;
}

export const PopButton: React.FC<PopButtonProps> = ({
  palette,
  popVariant = "primary",
  isLoading = false,
  children,
  sx,
  ...props
}) => {
  const { accentColor, isDarkMode } = usePaletteColors(palette);

  // --- 共通カラー設定 (ItemCardと統一) ---
  // 影の色
  const shadowColor = isDarkMode ? "#000000" : "#94a3b8";
  // 枠線の色（ItemCardと同じグレー）
  const borderColor = isDarkMode ? "#374151" : "#cbd5e1";

  const borderRadius = 2;

  // --- スタイル計算 ---
  let variantStyle = {};

  if (popVariant === "primary") {
    // ① プライマリバージョン（修正版）
    // 背景色あり、枠線はグレー、影付き
    variantStyle = {
      bgcolor: accentColor,
      color: "#ffffff",

      // 二重線をやめて、ItemCardと同じグレーの枠線にする
      border: `2px solid ${borderColor}`,

      // シンプルな影のみにする
      boxShadow: `0 2px 0 0 ${shadowColor}`,

      "&:hover": {
        bgcolor: accentColor,
        filter: "brightness(1.1)", // ホバー時は少し明るく
        // 枠線を少し濃くして強調（お好みで調整可）
        borderColor: isDarkMode ? "#6b7280" : "#94a3b8",
      },
      "&:active": {
        // 白と同じように沈み込む
        transform: !isLoading ? "translateY(2px)" : "none",
        boxShadow: !isLoading ? `0 2px 0 0 ${shadowColor}` : undefined,
      },
    };
  } else {
    // ② 白バージョン（変更なし）
    variantStyle = {
      bgcolor: isDarkMode ? "#1f2937" : "#ffffff",
      color: accentColor,
      border: `2px solid ${accentColor}`,
      boxShadow: `0 2px 0 0 ${shadowColor}`,

      "&:hover": {
        bgcolor: isDarkMode ? "#374151" : "#f8fafc",
      },
      "&:active": {
        transform: !isLoading ? "translateY(2px)" : "none",
        boxShadow: !isLoading ? `0 2px 0 0 ${shadowColor}` : undefined,
      },
    };
  }

  return (
    <Button
      disableElevation
      disabled={isLoading || props.disabled}
      sx={{
        ...variantStyle,
        borderRadius: borderRadius,
        textTransform: "none",
        fontWeight: "bold",
        fontSize: "0.75rem",
        px: 3,
        py: 1,
        transition:
          "transform 0.1s, box-shadow 0.1s, filter 0.2s, border-color 0.2s",

        // 無効時のスタイル
        "&.Mui-disabled": {
          bgcolor: isDarkMode ? "#374151" : "#e2e8f0",
          color: "#94a3b8",
          border: `2px solid ${isDarkMode ? "#4b5563" : "#cbd5e1"}`,
          boxShadow: "none",
          transform: "translateY(2px)",
        },

        ...sx,
      }}
      {...props}
    >
      {isLoading && (
        <CircularProgress
          size={24}
          color="inherit"
          sx={{ position: "absolute" }}
        />
      )}

      <span
        style={{
          opacity: isLoading ? 0 : 1,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {children}
      </span>
    </Button>
  );
};
